import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, X } from 'lucide-react';

// US States with abbreviations for autocomplete
export const US_STATES = [
  { abbr: 'AL', name: 'Alabama' }, { abbr: 'AK', name: 'Alaska' }, { abbr: 'AZ', name: 'Arizona' },
  { abbr: 'AR', name: 'Arkansas' }, { abbr: 'CA', name: 'California' }, { abbr: 'CO', name: 'Colorado' },
  { abbr: 'CT', name: 'Connecticut' }, { abbr: 'DE', name: 'Delaware' }, { abbr: 'FL', name: 'Florida' },
  { abbr: 'GA', name: 'Georgia' }, { abbr: 'HI', name: 'Hawaii' }, { abbr: 'ID', name: 'Idaho' },
  { abbr: 'IL', name: 'Illinois' }, { abbr: 'IN', name: 'Indiana' }, { abbr: 'IA', name: 'Iowa' },
  { abbr: 'KS', name: 'Kansas' }, { abbr: 'KY', name: 'Kentucky' }, { abbr: 'LA', name: 'Louisiana' },
  { abbr: 'ME', name: 'Maine' }, { abbr: 'MD', name: 'Maryland' }, { abbr: 'MA', name: 'Massachusetts' },
  { abbr: 'MI', name: 'Michigan' }, { abbr: 'MN', name: 'Minnesota' }, { abbr: 'MS', name: 'Mississippi' },
  { abbr: 'MO', name: 'Missouri' }, { abbr: 'MT', name: 'Montana' }, { abbr: 'NE', name: 'Nebraska' },
  { abbr: 'NV', name: 'Nevada' }, { abbr: 'NH', name: 'New Hampshire' }, { abbr: 'NJ', name: 'New Jersey' },
  { abbr: 'NM', name: 'New Mexico' }, { abbr: 'NY', name: 'New York' }, { abbr: 'NC', name: 'North Carolina' },
  { abbr: 'ND', name: 'North Dakota' }, { abbr: 'OH', name: 'Ohio' }, { abbr: 'OK', name: 'Oklahoma' },
  { abbr: 'OR', name: 'Oregon' }, { abbr: 'PA', name: 'Pennsylvania' }, { abbr: 'RI', name: 'Rhode Island' },
  { abbr: 'SC', name: 'South Carolina' }, { abbr: 'SD', name: 'South Dakota' }, { abbr: 'TN', name: 'Tennessee' },
  { abbr: 'TX', name: 'Texas' }, { abbr: 'UT', name: 'Utah' }, { abbr: 'VT', name: 'Vermont' },
  { abbr: 'VA', name: 'Virginia' }, { abbr: 'WA', name: 'Washington' }, { abbr: 'WV', name: 'West Virginia' },
  { abbr: 'WI', name: 'Wisconsin' }, { abbr: 'WY', name: 'Wyoming' }
];

export function AutocompleteInput({ 
  value, 
  onChange, 
  options = [], 
  placeholder = 'Type to search...', 
  className = '',
  disabled = false,
  showClear = true
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [filteredOptions, setFilteredOptions] = useState([]);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    if (inputValue) {
      const filtered = options.filter(opt => 
        opt.toLowerCase().includes(inputValue.toLowerCase())
      ).slice(0, 10);
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(options.slice(0, 10));
    }
  }, [inputValue, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsOpen(true);
    onChange(newValue);
  };

  const handleSelect = (option) => {
    setInputValue(option);
    onChange(option);
    setIsOpen(false);
  };

  const handleClear = () => {
    setInputValue('');
    onChange('');
    inputRef.current?.focus();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full h-10 px-3 pr-8 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#5DADE2] focus:border-transparent disabled:bg-slate-100 disabled:cursor-not-allowed ${className}`}
        />
        {showClear && inputValue && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded"
          >
            <X className="h-4 w-4 text-slate-400" />
          </button>
        )}
      </div>
      
      {isOpen && filteredOptions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          {filteredOptions.map((option, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handleSelect(option)}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center justify-between ${
                option === inputValue ? 'bg-sky-50 text-[#5DADE2]' : 'text-slate-700'
              }`}
            >
              <span>{option}</span>
              {option === inputValue && <Check className="h-4 w-4" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function StateAutocomplete({ value, onChange, placeholder = 'Select state...', className = '' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [filteredStates, setFilteredStates] = useState(US_STATES);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Get display value from abbreviation
  const getDisplayValue = (abbr) => {
    if (!abbr) return '';
    const state = US_STATES.find(s => s.abbr === abbr);
    return state ? `${state.name} (${state.abbr})` : abbr;
  };

  useEffect(() => {
    if (inputValue) {
      const filtered = US_STATES.filter(s => 
        s.name.toLowerCase().includes(inputValue.toLowerCase()) ||
        s.abbr.toLowerCase().includes(inputValue.toLowerCase())
      );
      setFilteredStates(filtered);
    } else {
      setFilteredStates(US_STATES);
    }
  }, [inputValue]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setInputValue('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setIsOpen(true);
  };

  const handleSelect = (state) => {
    onChange(state.abbr);
    setInputValue('');
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setInputValue('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative">
        {value && !isOpen ? (
          <div 
            onClick={() => { setIsOpen(true); inputRef.current?.focus(); }}
            className={`w-full h-10 px-3 pr-8 rounded-lg border border-slate-200 flex items-center cursor-pointer hover:border-slate-300 ${className}`}
          >
            <span className="text-sm text-slate-900">{getDisplayValue(value)}</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleClear(); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded"
            >
              <X className="h-4 w-4 text-slate-400" />
            </button>
          </div>
        ) : (
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => setIsOpen(true)}
            placeholder={placeholder}
            className={`w-full h-10 px-3 pr-8 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#5DADE2] focus:border-transparent ${className}`}
          />
        )}
        <ChevronDown className="absolute right-8 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
      </div>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          {filteredStates.length > 0 ? (
            filteredStates.map((state) => (
              <button
                key={state.abbr}
                type="button"
                onClick={() => handleSelect(state)}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center justify-between ${
                  state.abbr === value ? 'bg-sky-50 text-[#5DADE2]' : 'text-slate-700'
                }`}
              >
                <span>{state.name} ({state.abbr})</span>
                {state.abbr === value && <Check className="h-4 w-4" />}
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-slate-500">No states found</div>
          )}
        </div>
      )}
    </div>
  );
}

export function UnifiedSearchInput({ 
  value, 
  onChange, 
  locationOptions = { cities: [], states: [], zip_codes: [] },
  placeholder = 'Search by city, state, or ZIP...',
  className = ''
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    if (!inputValue || inputValue.length < 1) {
      setSuggestions([]);
      return;
    }

    const query = inputValue.toLowerCase().trim();
    const newSuggestions = [];

    // Check if it looks like a ZIP code (numeric)
    const isNumeric = /^\d+$/.test(query);

    if (isNumeric) {
      // Search ZIP codes
      const matchingZips = locationOptions.zip_codes
        .filter(z => z.startsWith(query))
        .slice(0, 5)
        .map(z => ({ type: 'zip', label: z, value: z }));
      newSuggestions.push(...matchingZips);
    } else {
      // Search states first (higher priority)
      const matchingStates = US_STATES
        .filter(s => 
          s.name.toLowerCase().includes(query) ||
          s.abbr.toLowerCase().includes(query)
        )
        .slice(0, 3)
        .map(s => ({ type: 'state', label: `${s.name} (${s.abbr})`, value: s.abbr }));
      newSuggestions.push(...matchingStates);

      // Then search cities
      const matchingCities = locationOptions.cities
        .filter(c => c.toLowerCase().includes(query))
        .slice(0, 5)
        .map(c => ({ type: 'city', label: c, value: c }));
      newSuggestions.push(...matchingCities);
    }

    setSuggestions(newSuggestions);
  }, [inputValue, locationOptions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsOpen(true);
    // Don't trigger filter until selection or blur
  };

  const handleSelect = (suggestion) => {
    setInputValue(suggestion.label);
    onChange(suggestion.value, suggestion.type);
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Apply filter with current input
      onChange(inputValue, null);
      setIsOpen(false);
    }
  };

  const handleBlur = () => {
    // Small delay to allow click on dropdown
    setTimeout(() => {
      if (inputValue && inputValue !== value) {
        onChange(inputValue, null);
      }
    }, 200);
  };

  const handleClear = () => {
    setInputValue('');
    onChange('', null);
    inputRef.current?.focus();
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'state': return '🏛️';
      case 'zip': return '📍';
      default: return '🏙️';
    }
  };

  const getTypeLabel = (type) => {
    switch(type) {
      case 'state': return 'State';
      case 'zip': return 'ZIP Code';
      default: return 'City';
    }
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => setIsOpen(true)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={`w-full rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#5DADE2] focus:border-transparent ${className}`}
        data-testid="search-input"
      />
      {inputValue && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded"
        >
          <X className="h-4 w-4 text-slate-400" />
        </button>
      )}
      
      {isOpen && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-auto">
          {suggestions.map((suggestion, index) => (
            <button
              key={`${suggestion.type}-${suggestion.value}-${index}`}
              type="button"
              onClick={() => handleSelect(suggestion)}
              className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-center gap-3 border-b border-slate-100 last:border-b-0"
            >
              <span className="text-lg">{getTypeIcon(suggestion.type)}</span>
              <div className="flex-1">
                <div className="text-sm font-medium text-slate-900">{suggestion.label}</div>
                <div className="text-xs text-slate-500">{getTypeLabel(suggestion.type)}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default AutocompleteInput;
